import { Company, Price, StoredObject } from "@/features/types";
import { Formik } from "formik";
import { Grid, Heading } from "@chakra-ui/react";
import AppListHeader from "@/features/ui/app-list-header";
import React, { useCallback, useMemo, useRef } from "react";
import { DEFAULT_SEASON } from "@/features/line-sheet-sets/app-season-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  useBankRemittanceReceiptDumbTransaction
} from "@/features/invoices/bank-remittance-receipts/hooks/use-bank-remittance-receipt-dumb-transaction";
import useIdentity from "@/features/ui/hooks/use-identity";
import useBankRemittanceReceiptFormValidationSchema
  from "@/features/invoices/bank-remittance-receipts/hooks/use-bank-remittance-receipt-form-validation-schema";
import { FormikUtils } from "@/features/ui/utils/formik-utils";
import useGetIssuedThroughOrIssuedTo from "@/features/invoices/hooks/use-get-issued-through-or-issued-to";
import { PriceInputValue } from "@/features/invoices/app-price-input";
import { DepositInvoice } from "@/features/invoices/deposit-invoices/deposit-invoice.type";
import PriceUtils from "@/features/ui/utils/price-utils";
import { InvoiceUtils } from "@/features/invoices/invoice-utils";
import { useRecoilValue } from "recoil";
import { exchangeCurrencyStore } from "@/shared/store/exchangeCurrencyStore";
import AppButton from "@/features/line-sheet-sets/app-button";
import BankRemittanceReceiptFormModal
  from "@/features/invoices/bank-remittance-receipts/bank-remittance-receipt-form-modal";
import { LightPayment } from "@/features/invoices/payment/payment.type";

export interface BankRemittanceReceiptFormFields {
  id?: number;
  number?: string;
  name?: string;
  season?: string;
  orderedBy?: Pick<Company, "id" | "name">;
  issuedThrough?: Pick<Company, "id" | "name">;
  issuedOn: Date;
  publicComment?: string;
  privateComment?: string;
  sender?: Company;
  receiver?: Company;
  file?: File | Pick<StoredObject, "id" | "name">;
  amount?: PriceInputValue;
  bankFee?: PriceInputValue;
  exchangeRate?: number;
  payments: {
    id: number;
    name: string;
    amount: Price;
    unremittedAmount: Price;
  }[];
  depositInvoice?: DepositInvoice;
  bankRemittanceReceiptDetailList: {
    id: number;
    amount?: PriceInputValue;
    payment?: {
      id: number;
      name: string;
    };
  }[];
}

export default function UploadBankRemittanceReceiptPageModal(
  {
    payment,
    handleClose
  }: {
    payment: LightPayment;
    handleClose: () => void;
  }
) {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const { withMessage } = useYupHelper({
    ns: "bank_remittance_receipts",
    prefix: "update_bank_remittance_receipt_form",
  });
  const { navigate } = useRouterHelper();

  const validateSchema = useBankRemittanceReceiptFormValidationSchema();

  const validate = useCallback(
    async (values: BankRemittanceReceiptFormFields) => {
      try {
        await validateSchema.validate(values, {
          abortEarly: false,
          context: { paymentType: payment.type } as any,
        });
      } catch (e) {
        showError(e);
        return e;
      }
    },
    [validateSchema, showError, payment.type]
  );

  const { addCreate } = useBankRemittanceReceiptDumbTransaction();

  const handleSubmit = useCallback(
    (values: BankRemittanceReceiptFormFields) => {
      if (identity?.company) {
        addCreate(
          {
            by: identity.company.type,
            companyId: identity.company.id,
          },
          {
            number: values.number ?? null,
            name: values.name!,
            season: values.season!,
            issuedToCompany: values.orderedBy!,
            issuedThroughCompany: values.issuedThrough!,
            issuedOn: values.issuedOn!,
            publicComment: values.publicComment || null,
            privateComment: values.privateComment || null,
            file: values.file instanceof File ? values.file : null,
            sender: values.sender!,
            receiver: values.receiver!,
            amount: PriceUtils.placeholder(
              values.amount,
              InvoiceUtils.getRemittanceCurrency(values)
            ),
            bankFee: PriceUtils.placeholder(
              values.bankFee,
              "KRW"
            ),
            exchangeRate: values.exchangeRate || 1,
            payments: values.payments.map((payment) => {
              return {
                id: payment.id,
              };
            }),
            bankRemittanceReceiptDetailList:
              values.bankRemittanceReceiptDetailList.map((detail) => {
                return {
                  amount: detail.amount as Price,
                  payment: {
                    id: detail.payment!.id,
                    name: detail.payment!.name,
                  },
                };
              }),
          }
        );
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 500);
      });
    },
    [addCreate, identity]
  );

  const issuedThroughOrIssuedTo = useGetIssuedThroughOrIssuedTo();
  const exchangeCurrencyInfo = useRecoilValue(exchangeCurrencyStore);
  // 원화 송금(원화 프로포마의 바이어->부티크 등)은 환전·해외 송금 수수료가 없다
  const isKrwPayment = payment.amount.currency === "KRW";
  const initialExchangeRateRef = useRef(
    isKrwPayment
      ? 1
      : exchangeCurrencyInfo["EUR"]?.remittance_send_rate !== undefined
      ? Math.round(exchangeCurrencyInfo["EUR"].remittance_send_rate)
      : undefined
  );

  const initialValues = useMemo((): BankRemittanceReceiptFormFields => {
    return {
      id: -1,
      season: DEFAULT_SEASON,
      issuedOn: FormikUtils.initialIssuedOnFunc(),
      bankRemittanceReceiptDetailList: [
        {
          id: -1,
          amount: {
            value: payment.unremittedAmount.value,
            currency: payment.amount.currency
          },
          payment: payment
        }
      ],
      amount: PriceUtils.placeholder(
        {
          value: payment.unremittedAmount.value,
          currency: payment.amount.currency
        },
        // 송금 요청 통화 그대로(원화 프로포마의 바이어->부티크 송금은 원화)
        payment.amount.currency
      ),
      bankFee: PriceUtils.placeholder(
        {
          currency: "KRW",
          value: isKrwPayment ? 0 : 43000,
        },
        "KRW"
      ),
      exchangeRate: initialExchangeRateRef.current,
      ...issuedThroughOrIssuedTo,
      // 경유 에이전시는 송금 요청에 이미 정해져 있다 — 로그인 회사로 채우면 부티크는 비어서
      // '에이전시를 선택해야 한다'로 막힌다
      ...(payment.issuedThroughCompany
        ? {
            issuedThrough: {
              id: payment.issuedThroughCompany.id,
              name: payment.issuedThroughCompany.name,
            },
          }
        : {}),
      orderedBy: {
        id: payment.issuedToCompany.id,
        name: payment.issuedToCompany.name,
      },
      sender: payment.sender,
      receiver: payment.receiver,
      payments: [
        payment
      ]
    };
  }, [issuedThroughOrIssuedTo, payment]);

  return (
    <Formik<BankRemittanceReceiptFormFields>
      enableReinitialize={true}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      validate={validate}
      onSubmit={(values) => {
        return handleSubmit(values);
      }}
    >
      {(props) => {
        return (
          <Grid
            as={"form"}
            height={"100%"}
            width={"100%"}
            gridTemplateColumns={"1fr"}
            gridTemplateRows={"auto auto 1fr"}
          >
            <AppListHeader
              headers={[
                <Heading key={"header"} as={"h2"} fontSize={"14px"}>
                  {tTitle(
                    "bank_remittance_receipts:upload_bank_remittance_receipt"
                  )}
                </Heading>,
              ]}
              filters={[]}
              actions={[
                <AppButton
                  key={`Cancel`}
                  isDisabled={props.isSubmitting}
                  onClick={async () => {
                    handleClose();
                  }}
                >
                  {tTitle("cancel")}
                </AppButton>,
                <AppButton
                  key={`Upload`}
                  variant={"primary"}
                  isLoading={props.isSubmitting}
                  onClick={async () => {
                    const result = await props.submitForm();
                    // @ts-ignore
                    if (result) {
                      handleClose();
                    }
                  }}
                >
                  {tTitle("upload")}
                </AppButton>
              ]}
              showNavigateToBack={false}
              emptyStackPage={"/bank-remittance-receipts"}
              blockNavigateToBack={props.isSubmitting || props.dirty}
            />
            <BankRemittanceReceiptFormModal formik={props} isEditing={true} />
          </Grid>
        );
      }}
    </Formik>
  );
}
